import type { SkillGroup } from '@sfc/sdk';

/**
 * Choosing from the list, rather than typing into a box.
 *
 * Free text would be easier to build and would make the deck useless: "React",
 * "react", "ReactJS" and "front end" are four things to a database and one
 * thing to a person, and the ranking is a count of exact matches. The cost is
 * that somebody whose craft is not on the list cannot say so, which is a real
 * cost and the reason the list is reviewed as a file rather than typed into a
 * console.
 *
 * The ceiling is said out loud rather than enforced silently. Somebody who has
 * picked twelve and wants a thirteenth should be told why they cannot, not
 * left clicking a checkbox that does nothing.
 */
export function SkillPicker({
  groups,
  chosen,
  max,
  onChange,
}: {
  groups: SkillGroup[];
  /** Slugs, which is what both endpoints take. */
  chosen: string[];
  max: number;
  onChange: (next: string[]) => void;
}): React.JSX.Element {
  const full = chosen.length >= max;

  function toggle(slug: string): void {
    if (chosen.includes(slug)) onChange(chosen.filter((one) => one !== slug));
    else if (!full) onChange([...chosen, slug]);
  }

  return (
    <div className="picker" data-testid="skill-picker">
      <p className="fine picker__count" aria-live="polite">
        {chosen.length} of {max} chosen
        {full ? ' · remove one to pick another' : ''}
      </p>

      {groups.map((group) => (
        <fieldset key={group.discipline} className="picker__group">
          <legend className="card__label">{group.discipline}</legend>
          <div className="picker__options">
            {group.skills.map((skill) => {
              const picked = chosen.includes(skill.slug);
              return (
                <label
                  key={skill.id}
                  className={`pick${picked ? ' pick--on' : ''}${!picked && full ? ' pick--full' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={picked}
                    disabled={!picked && full}
                    onChange={() => toggle(skill.slug)}
                  />
                  {skill.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
